// src/pages/matching-intergenerationnel/ScoringMatching.jsx
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ProfilCanvas from './ProfilCanvas';
import HeirProfilCard from "../../components/matching-intergenerationnel/HeirProfilCard"; 

async function verifyHeirTokenIfPresent() {
  try {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('t');
    if (!t) return { ok: true, viaToken: false };
    // grâce au proxy CRA, pas besoin de credentials:true
    const r = await fetch(`/api/heirs/verify?t=${encodeURIComponent(t)}`, {
      method: 'GET'
    });
    const data = await r.json();
    if (!r.ok) return { ok: false, error: data?.error || 'invalid' };
    return { ok: true, viaToken: true, beneficiary_id: data.beneficiary_id };
  } catch (e) {
    return { ok: false, error: String(e) };
  }
}

// Utilisé uniquement quand aucune session héritier réelle n'est active
// (ex : /matching-intergenerationnel/demo, qui injecte un profil directement
// en state sans passer par /api/heirs/verify) — sert de filet de secours pour
// que la démo reste présentable même sans collection réelle à interroger.
const FALLBACK_ARTWORKS = [
  {
    id: "1",
    title: "Composition moderne",
    artist: "Artiste Test 1",
    style: "Contemporary",
    description: "Une œuvre pleine de couleur et d'émotion moderne",
    axes: { ouverture: 80, esthetique: 80, experience: 70, collectif: 40, rapport_au_temps: "futur" },
    keywords: ["modernité", "couleur", "émotion"]
  },
  {
    id: "2",
    title: "Paysage classique",
    artist: "Artiste Test 2",
    style: "Classical",
    description: "Un paysage traditionnel apaisant",
    axes: { ouverture: 30, esthetique: 60, experience: 30, collectif: 60, rapport_au_temps: "passe" },
    keywords: ["tradition", "paysage", "nature"]
  },
  {
    id: "3",
    title: "Abstraction colorée",
    artist: "Artiste Test 3",
    style: "Abstract",
    description: "Une explosion de couleurs et de modernité",
    axes: { ouverture: 85, esthetique: 90, experience: 90, collectif: 35, rapport_au_temps: "present" },
    keywords: ["abstraction", "couleur", "modernité"]
  },
  {
    id: "4",
    title: "Portrait contemporain",
    artist: "Artiste Test 4",
    style: "Contemporary",
    description: "Un portrait moderne exprimant des émotions profondes",
    axes: { ouverture: 65, esthetique: 70, experience: 70, collectif: 80, rapport_au_temps: "present" },
    keywords: ["portrait", "contemporain", "émotion"]
  }
];

// Scoring pour les œuvres de démo (FALLBACK_ARTWORKS), qui portent des axes
// psychologiques fictifs comparables à ceux du profil héritier.
function computeMatchScore(heirAxes, artwork) {
  let score = 0;
  if (!heirAxes) return 50;

  // Axes numériques : plus c'est proche, mieux c'est
  ["ouverture", "esthetique", "experience", "collectif"].forEach(axis => {
    score += 100 - Math.abs((heirAxes[axis] || 50) - (artwork.axes[axis] || 50));
  });

  // Bonus pour rapport au temps matching
  score += heirAxes.rapport_au_temps === artwork.axes.rapport_au_temps ? 50 : 0;

  // Bonus pour matching mots-clés
  if (artwork.keywords && heirAxes.keywords) {
    const matches = artwork.keywords.filter(kw =>
      heirAxes.keywords.map(k => k.toLowerCase()).includes(kw.toLowerCase())
    );
    score += matches.length * 20;
  }
  return Math.min(100, Math.round(score / 5.5)); // Score sur 0-100
}

// Scoring pour les vraies œuvres de la collection (via /api/heirs/artworks) :
// ces fiches n'ont pas d'axes psychologiques (pas d'enrichissement IA en
// base pour l'instant), donc on retombe sur un recoupement texte simple
// entre les mots-clés du profil héritier et le titre/artiste/description.
// Base neutre à 50% tant qu'aucun mot-clé ne matche, pour ne pas laisser
// croire à une précision qu'on n'a pas.
function computeTextMatchScore(heirKeywords, artwork) {
  if (!heirKeywords || heirKeywords.length === 0) return 50;
  const haystack = `${artwork.title || ""} ${artwork.artist || ""} ${artwork.description || ""}`.toLowerCase();
  const matches = heirKeywords.filter(kw => haystack.includes(String(kw).toLowerCase()));
  return Math.min(100, 50 + matches.length * 15);
}

export default function ScoringMatching() {
  const location = useLocation();
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heirProfile, setHeirProfile] = useState(null);
  const [error, setError] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    (async () => {
      // 1) vérifie le token si présent (pose le cookie heir_session)
      const verify = await verifyHeirTokenIfPresent();
      if (!verify.ok) {
        setError('Lien invalide ou expiré');
        setLoading(false);
        return;
      }

      // 2) récupère le profil depuis location.state si dispo, sinon fais simple (démo)
      const profileFromState = location.state?.heirProfile || null;
      if (profileFromState) setHeirProfile(profileFromState);

      // On prépare un profil effectif LOCAL pour le calcul
      let effectiveProfile = profileFromState;
      if (!effectiveProfile) {
        try {
          const me = await fetch('/api/heirs/me'); // cookie HttpOnly envoyé auto
          if (me.ok) {
            const data = await me.json();
            if (data?.profile) {
              // on met à jour le state pour l'UI…
              setHeirProfile(data.profile);
              // …et on utilise le profil chargé tout de suite pour le calcul
              effectiveProfile = data.profile;
            }
          }
        } catch {}
      }

      // 3) calcule les affinités

      // Choisir les données de calcul (state prioritaire, sinon profil chargé via /api/heirs/me)
      const axesForCalc =
        effectiveProfile?.axes
        || (heirProfile?.axes ?? null);

      const keywordsForCalc =
        effectiveProfile?.artKeywords
        || (heirProfile?.artKeywords ?? []);

      // 4) essaie de charger la vraie collection du titulaire (nécessite une
      // session héritier réelle, posée par /api/heirs/verify). Si absente
      // (ex : démo directe en state) ou vide, on retombe sur FALLBACK_ARTWORKS.
      let realArtworks = [];
      try {
        const r = await fetch('/api/heirs/artworks');
        if (r.ok) {
          const data = await r.json();
          realArtworks = data?.artworks || [];
        }
      } catch {}

      const sorted = realArtworks.length > 0
        ? realArtworks
            .map(a => ({ ...a, affinityScore: computeTextMatchScore(keywordsForCalc, a) }))
            .sort((a, b) => b.affinityScore - a.affinityScore)
        : FALLBACK_ARTWORKS
            .map(a => ({ ...a, affinityScore: computeMatchScore({ ...axesForCalc, keywords: keywordsForCalc }, a) }))
            .sort((a, b) => b.affinityScore - a.affinityScore);

      setArtworks(sorted);
      setLoading(false);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Calcul de vos affinités artistiques...</p>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 font-medium">{error}</p>
          <button onClick={() => (window.location.href = '/unauthorized')}
                  className="mt-4 px-4 py-2 bg-gray-700 text-white rounded">
            Revenir
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-4">Vos Recommandations Artistiques</h1>
        {heirProfile && (
          <>
            <div className="bg-blue-50 p-4 rounded-lg mb-6">
              <p className="text-blue-800 font-medium">
                Profil : {heirProfile.age} ans, {heirProfile.situation}, {heirProfile.domaine}
              </p>
              {heirProfile.archetype && (
                <p className="mt-2 text-sm text-blue-700">
                  Archétype : <span className="font-semibold">{heirProfile.archetype.content.name}</span>
                  {" — "}{heirProfile.archetype.content.tagline}
                </p>
              )}
              <div className="text-blue-700 mt-2">Mots-clés : {heirProfile.artKeywords?.join(", ")}</div>
              {heirProfile.artMessage && (
                <p className="text-blue-600 text-sm mt-2 italic">"{heirProfile.artMessage}"</p>
              )}
            </div>
            {/* CANVAS GRAPHIQUE — 9 dimensions réelles */}
            <ProfilCanvas dims={heirProfile.dims} />
            {heirProfile && <HeirProfilCard heirProfile={heirProfile} />}
          </>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {artworks.map((artwork, idx) => (
          <div key={artwork.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
            {artwork.image_url && (
              <img
                src={artwork.image_url}
                alt={artwork.title}
                className="w-full h-48 object-cover rounded-md mb-4"
              />
            )}
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1">
                <h3 className="text-lg font-semibold mb-1">{artwork.title}</h3>
                <p className="text-gray-600 text-sm">Par : {artwork.artist}</p>
              </div>
              <div className="flex flex-col items-end">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  artwork.affinityScore >= 80
                    ? 'bg-green-100 text-green-800'
                    : artwork.affinityScore >= 60
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {artwork.affinityScore}% de match
                </span>
                <span className="text-xs text-gray-500 mt-1">#{idx + 1}</span>
              </div>
            </div>
            <p className="text-sm text-gray-500 mb-4">{artwork.description}</p>
            <div className="flex items-center justify-between text-xs text-gray-400">
              <span>{artwork.style || artwork.medium ? `Style : ${artwork.style || artwork.medium}` : ''}</span>
              <span>
                {artwork.affinityScore >= 80 ? '🎯 Excellent match' :
                  artwork.affinityScore >= 60 ? '👍 Bon match' :
                    '🤔 Match modéré'}
              </span>
            </div>
          </div>
        ))}
      </div>
      <div className="text-center mt-8">
        <button
          onClick={() => window.history.back()}
          className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 mr-4"
        >Retour</button>
        <button className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          Voir plus d'œuvres
        </button>
      </div>
    </div>
  );
}
